import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Injector,
  Input,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../../core/h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../core/h3ml/state/task-workspace-store';
import { TransientShapeLayerState } from '../../../../../core/outputs/annotation/annotation-shared/state/layers/transient-shape-layer-state';
import { ObjectlessFocusedShapeService } from '../../../../../core/outputs/annotation/annotation-shared/utils/objectless-focused-shape.service';
import { ShapeState } from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { isTransientShapeLayerState } from '../../state/layers/layer-utils';
import { AbstractAnnotationStore } from '../../state/root/abstract-annotation-store';

@Component({
  selector: 'h3-shape-list-and-tag-editor',
  templateUrl: './shape-list-and-tag-editor.component.html',
  styleUrls: ['./shape-list-and-tag-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShapeListAndTagEditorComponent extends DirectiveSuperclass {
  @Input() isMultiFrame = true;

  shapeListHidden$ = this.getTransientsState$('shapeListHidden');
  tagEditorHidden$ = this.getTransientsState$('tagEditorHidden');
  shapeFiltersHidden$ = this.getTransientsState$('shapeFiltersHidden');
  focusedStoreChanged$: Observable<Array<Store<ShapeState> | undefined>>;
  isObjectEnabledProject = this.taskWorkspaceStore.isUsingObjects();
  // we can remove this once the object list is moved outside the output, this
  // facilitates tests since there will only be one instance of the object list
  // in sensor fusion
  displayObjectList$ = combineLatest([
    this.taskWorkspaceStore('selectedOutputName').$,
    this.taskWorkspaceStore('splitViewOutputName').$,
  ]).pipe(
    map(
      ([outputName, splitViewOutputName]) =>
        isDefined(splitViewOutputName) ||
        this.outputInfoStore.state().output.name === outputName,
    ),
  );

  constructor(
    private store: AbstractAnnotationStore,
    private taskWorkspaceStore: TaskWorkspaceStore,
    @Inject(outputInfoStoreToken) private outputInfoStore: Store<OutputInfo>,
    focusedShapeService: ObjectlessFocusedShapeService,
    injector: Injector,
  ) {
    super(injector);
    this.focusedStoreChanged$ = focusedShapeService
      .getFocusedShapeStore$()
      .pipe(map((shapeStore) => [shapeStore]));
  }

  private getTransientsState$(transientKey: keyof TransientShapeLayerState) {
    return this.store.getActiveLayer$().pipe(
      filter(isDefined),
      map((layerState) => {
        const transient = layerState.transient;
        assert(
          isTransientShapeLayerState(transient),
          'active transient layer should be a shape transient layer when there is a shape list',
        );
        return transient[transientKey];
      }),
    );
  }
}
